import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import SalonFlag from 'components/core/SalonFlag.component';
import Badge from 'components/generic/Badge.component';
import ResponsiveBox from 'components/generic/ResponsiveBox.component';
import LINK from 'constants/link.const';
import PLAN_NAME from 'constants/plan.const';
import TRACK_EVENT_TYPE from 'constants/salon/trackEventType.const';
import SF from 'constants/salonFlag.const';
import S from 'constants/string.const';
import useIsFromMobileApp from 'hooks/useIsFromMobileApp.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectCurrentSalonPricingPlan } from 'selectors/currentSalon.selectors';
import { isEffectiveReceptionist } from 'selectors/user';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import linkTo from 'utils/links/linkTo.util';

import { trackEvent } from 'tracking/actions';

import CN from './CommunicationsHeader.module.scss';

const MenuItem = ({ icon, children, disabled, ...extra }) => {
  const menuItemDisabledClass = disabled ? CN.menuItemDisabled : CN.menuItem;
  return (
    <NavLink {...extra} className={menuItemDisabledClass} activeClassName={CN.selected}>
      <div className={CN.hoverContainer}>
        <div className={CN.menuText}>
          <div className={CN[`${icon}Icon`]} />
          <div>{children}</div>
        </div>
      </div>
    </NavLink>
  );
};

const CommunicationsHeader = ({ isReceptionist, bucketType, trackEvent, plan }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const salonId = useCurrentSalonId();
  const isFromMobile = useIsFromMobileApp();

  const componentClasses = cn({
    [CN.component]: true,
    [CN.dark]: isDarkMode,
  });

  const marketingLink = linkTo({ pattern: LINK.marketing, params: { salonId }, fault: '#' });
  const clientsLink = `/salon/${salonId}/communications/clients/${bucketType}`;
  const campaignsLink = linkTo({ pattern: LINK.campaigns, params: { salonId }, fault: '#' });
  const autoLink = linkTo({ pattern: LINK.clientConnect, params: { salonId }, fault: '#' });
  const remindersLink = linkTo({ pattern: LINK.reminders, params: { salonId }, fault: '#' });
  const emailsLink = linkTo({ pattern: LINK.emails, params: { salonId }, fault: '#' });
  const emailsLinkSplash = linkTo({ pattern: LINK.emailsSplash, params: { salonId }, fault: '#' });
  const emailsAnalyticsLink = linkTo({ pattern: LINK.emailsSent, params: { salonId }, fault: '#' });

  const handleMarketing = useCallback(() => void trackEvent(TRACK_EVENT_TYPE.marketingTab), [trackEvent]);
  const handleClients = useCallback(() => void trackEvent(TRACK_EVENT_TYPE.clientsTab), [trackEvent]);
  const handleCampaigns = useCallback(() => void trackEvent(TRACK_EVENT_TYPE.campaignsTab), [trackEvent]);
  const handleAuto = useCallback(() => void trackEvent(TRACK_EVENT_TYPE.autoTab), [trackEvent]);
  const handleReminders = useCallback(() => void trackEvent(TRACK_EVENT_TYPE.remindersTab), [trackEvent]);
  // TODO: trackEvent for emails

  const hasEmailSplash = plan === PLAN_NAME.essential;

  return (
    <div data-bem="CommunicationsHeader" className={componentClasses}>
      {!isReceptionist && (
        <div className={CN.container}>
          <MenuItem icon="chart" to={marketingLink} onClick={handleMarketing}>
            {t('marketing.headerNav.summary')}
          </MenuItem>

          {hasEmailSplash && (
            <MenuItem icon="envelope" to={emailsLinkSplash} onClick={alwaysNoop}>
              <div className={CN.newItem}>
                {t('marketing.headerNav.emailMarketing')}
                <ResponsiveBox.Desktop>
                  <Badge name={`${t('marketing.headerNav.new')} ${S.rocket}`} variant="menu" />
                </ResponsiveBox.Desktop>
              </div>
            </MenuItem>
          )}

          <MenuItem icon="group" to={clientsLink} onClick={handleClients}>
            {t('marketing.headerNav.allClients')}
          </MenuItem>

          {!isFromMobile && (
            <SalonFlag name={SF.emailMarketing}>
              <SalonFlag.IfEnabled>
                <>
                  <MenuItem icon="envelope" to={emailsLink} onClick={alwaysNoop}>
                    <div className={CN.newItem}>
                      {t('marketing.headerNav.emailBlast')}
                      <ResponsiveBox.Desktop>
                        <Badge name={`New ${S.rocket}`} variant="menu" />
                      </ResponsiveBox.Desktop>
                    </div>
                  </MenuItem>

                  <MenuItem icon="chartArrow" to={emailsAnalyticsLink} onClick={alwaysNoop}>
                    <div className={CN.newItem}>{t('marketing.headerNav.sentEmails')}</div>
                  </MenuItem>
                </>
              </SalonFlag.IfEnabled>
            </SalonFlag>
          )}

          <MenuItem icon="rocket" to={campaignsLink} onClick={handleCampaigns}>
            {t('marketing.headerNav.sentTexts')}
          </MenuItem>
          <MenuItem icon="bubble" to={autoLink} onClick={handleAuto}>
            {t('marketing.headerNav.automatedTexts')}
          </MenuItem>
          <MenuItem icon="megaphone" to={remindersLink} onClick={handleReminders}>
            {t('marketing.headerNav.notifications')}
          </MenuItem>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  isReceptionist: isEffectiveReceptionist(state),
  plan: selectCurrentSalonPricingPlan(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ trackEvent }, dispatch);

const CommunicationsHeaderHoc = connect(mapStateToProps, mapDispatchToProps)(CommunicationsHeader);

export default CommunicationsHeaderHoc;
